import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router";
import type { AppState, DeploymentStatus } from "../lib/types";
import { matchesQuery } from "../lib/search";
import { Icon, type IconName } from "./icons";

export function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(" ");
}

// ----------------------------------------------------------------- button

/*
 * The emphasis ladder, and there is no rung between these.
 *
 *   primary    the one action you opened the screen to take
 *   outline    promoted, but sharing the screen with a primary
 *   secondary  anything with a peer beside it
 *   ghost      row-level and tertiary; no chrome until hover
 *   danger     destructive, and only ever destructive
 *
 * `primary` is the solid inverse block because that is what the semantic layer
 * declares it to be — see `--color-inverse` in index.css. It used to be
 * brand-600 while the wizards, the auth pages and the sidebar each reached for
 * `inverse` instead, so a customer met two different primary buttons depending
 * on which screen they were on. One rung, one look.
 *
 * Freeing blue from button duty is the other half of it: `accent` now means a
 * link, an active tab, or focus, and nothing else, so a blue thing on a screen
 * is always something you can navigate to.
 */
type ButtonVariant = "primary" | "secondary" | "outline" | "ghost" | "danger" | "inverse";

const PRIMARY = "bg-inverse text-on-inverse hover:opacity-90 disabled:opacity-40";

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: PRIMARY,
  secondary:
    "border border-line bg-transparent text-fg hover:border-line-strong hover:bg-raised disabled:text-fg-subtle disabled:hover:bg-transparent",
  outline:
    "border border-accent/40 bg-transparent text-accent hover:border-accent hover:bg-accent-soft disabled:opacity-40",
  /* Dimmed when disabled like primary and danger, not merely a shade lighter:
     `fg-subtle` beside `fg-muted` read as enabled — a row of Restore buttons
     held while a restore ran looked exactly like one that would start another. */
  ghost:
    "text-fg-muted hover:bg-raised hover:text-fg disabled:text-fg-subtle disabled:opacity-50 disabled:hover:bg-transparent",
  danger:
    "border border-danger/40 bg-transparent text-danger hover:bg-danger/10 hover:border-danger disabled:opacity-40",
  /* Deprecated alias. Twelve call sites named `inverse` back when it was the
     only white block; they now get the same styles as `primary` by definition
     rather than by coincidence. Prefer `primary` in new markup. */
  inverse: PRIMARY,
};

/*
 * The second height in each pair is the thumb's. 28 and 32px are comfortable
 * under a cursor that lands where it is pointed and miss under a finger, which
 * is the same reasoning AppLayout already applies to the drawer and search
 * buttons — this extends it to every button rather than the two that happened
 * to be written by hand.
 *
 * `min-h` rather than `h`, so a button that wraps to two lines on a narrow
 * screen grows instead of clipping its own label.
 */
const BUTTON_SIZES = {
  sm: "h-7 pointer-coarse:h-auto pointer-coarse:min-h-11 gap-1.5 px-2.5 text-[13px]",
  md: "h-8 pointer-coarse:h-auto pointer-coarse:min-h-11 gap-2 px-3 text-[13px]",
  lg: "h-10 pointer-coarse:h-auto pointer-coarse:min-h-11 gap-2 px-4 text-sm",
  /* The auth surface, and it exists to match `xl` on the input rather than to
     be one step louder: a 44px field with a 40px button under it is a form
     nobody drew, and the misalignment is exactly the kind of thing you see
     before you can name it. */
  xl: "h-11 pointer-coarse:h-auto pointer-coarse:min-h-11 gap-2 px-5 text-sm",
};

/** Shared so anchors and <Link>s can look like buttons without nesting one. */
export function buttonStyles(
  variant: ButtonVariant = "primary",
  size: keyof typeof BUTTON_SIZES = "md",
  className?: string,
): string {
  return cx(
    "inline-flex items-center justify-center rounded-md font-medium whitespace-nowrap transition-colors duration-200 ease-smooth disabled:cursor-not-allowed",
    BUTTON_SIZES[size],
    BUTTON_VARIANTS[variant],
    className,
  );
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: keyof typeof BUTTON_SIZES;
  loading?: boolean;
  icon?: IconName;
}

/**
 * `type="button"` unless a caller asks for otherwise.
 *
 * HTML defaults a button inside a form to `submit`, which made every ordinary
 * control that happened to sit in one into a submit control. The copy button on
 * the tunnel wizard's first step was the one that surfaced it: clicking Copy on
 * `wg genkey` submitted the form with every field still empty, and the API
 * answered with a validation error for all five of them. On the last step the
 * same click would have created the tunnel.
 *
 * Safe as a default because every form in this app already marks its own submit
 * control explicitly — checked across all fifteen of them — so nothing was
 * relying on the implicit behaviour. Pressing Enter in a text field still
 * submits either way; that path does not go through a button's type.
 */
export function Button({
  variant = "primary",
  size = "md",
  loading = false,
  icon,
  disabled,
  className,
  children,
  type = "button",
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      type={type}
      disabled={disabled || loading}
      className={buttonStyles(variant, size, className)}
    >
      {loading ? (
        <Spinner className="size-3.5" />
      ) : icon ? (
        <Icon name={icon} className="size-3.5" />
      ) : null}
      {children}
    </button>
  );
}

export function Spinner({ className }: { className?: string }) {
  return (
    <svg
      className={cx("animate-spin", className ?? "size-5")}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.4 0 0 5.4 0 12h4z"
      />
    </svg>
  );
}

// ------------------------------------------------------------------ form

export function Field({
  label,
  hint,
  error,
  htmlFor,
  action,
  children,
}: {
  /** A node, so a prompt can set apart what it asks for — see `ConfirmPrompt`. */
  label: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  htmlFor?: string;
  /**
   * Sits at the far end of the label row. For the one control that has a way
   * out beside it — a password field and its reset link — which belongs level
   * with the label rather than stranded under the input, where it reads as a
   * caption on the field it is not about.
   */
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      {/* A row even with no action: one child in a `justify-between` flex sits
          exactly where the block label used to, so nothing else moves. */}
      <div className="flex items-baseline justify-between gap-3">
        <label htmlFor={htmlFor} className="block text-[13px] font-medium text-fg">
          {label}
        </label>
        {action}
      </div>
      {children}
      {error ? (
        <p className="flex items-center gap-1.5 text-[13px] text-danger">
          <Icon name="warning" className="size-3.5 shrink-0" />
          {error}
        </p>
      ) : hint ? (
        <p className="text-[13px] text-fg-subtle">{hint}</p>
      ) : null}
    </div>
  );
}

/*
 * One control style for every input.
 *
 * `canvas` rather than `raised`: a field is somewhere you type into, so it
 * reads as cut into the panel rather than sitting on it. The focus ring is
 * drawn with `ring` rather than the browser outline so it follows the field's
 * own rounding.
 *
 * `pointer-coarse:text-base` is the one piece of this that is not taste. iOS
 * Safari zooms the viewport in when it focuses a control whose text is under
 * 16px, and does not zoom back out on blur — so one tap on a 13px field leaves
 * a phone at 1.3x with the page wider than the screen and no way back but a
 * pinch. 16px costs the phone nothing: it is the size iOS was going to render
 * anyway, and zooming was how it got there.
 *
 * Keyed on the pointer and not a width breakpoint, because the trigger is the
 * input method rather than the viewport — a desktop window dragged narrow
 * never zooms, and keeps the density this was drawn at.
 */
/*
 * `pointer-coarse:min-h-11` for the thumb, and it does double duty: the
 * show/hide toggle on a password field is positioned `inset-y-0` against the
 * field, so it is exactly as tall as the field is and nothing else can make it
 * a 44px target.
 */
const CONTROL_BASE =
  "w-full rounded-md border border-line text-fg transition-colors duration-200 ease-smooth placeholder:text-fg-subtle hover:border-line-strong focus:border-accent focus:ring-2 focus:ring-accent/20 focus:outline-none disabled:cursor-not-allowed disabled:text-fg-subtle disabled:hover:border-line";

/*
 * Which surface the field is sitting on — and the fill is the inverse of it,
 * which is the whole reason this is a prop rather than a constant.
 *
 * A field inside a panel is cut *into* it, so it takes the darker canvas. A
 * field on the bare canvas has to stand *out* of it, so it takes surface. Same
 * component, opposite fills, and picking the wrong one paints an input exactly
 * the colour of the page behind it — visible only by its hairline, which is the
 * failure the sign-in form hit the moment its panel came off.
 *
 * The fill is deliberately not folded into the size map. Two background
 * utilities in one class list resolve by Tailwind's emission order rather than
 * by the order you wrote them, which the styling notes call out as always a bug
 * — so there is exactly one `bg-*` here and one place that chooses it.
 */
const CONTROL_SURFACES = {
  panel: "bg-canvas",
  canvas: "bg-surface",
};

/*
 * `md` is the density this app is drawn at: a field in a table row, a modal, a
 * settings panel, where the control is one of thirty things on screen.
 *
 * `xl` is the auth surface, where the control is one of three. Same colours,
 * same focus ring, same rounding — only the box grows, which is what keeps a
 * sign-in field recognisably the same component as an env-var field rather
 * than a second design. A fixed `h-11` rather than more padding, so it lands on
 * the 44px touch target by construction instead of via `pointer-coarse`.
 */
const CONTROL_SIZES = {
  md: "px-2.5 py-1.5 pointer-coarse:min-h-11 text-[13px] pointer-coarse:text-base",
  xl: "h-11 px-3.5 text-sm pointer-coarse:text-base",
};

const CONTROL = cx(CONTROL_BASE, CONTROL_SIZES.md, CONTROL_SURFACES.panel);

/*
 * `size` shadows the HTML attribute of the same name, which is why it is
 * omitted above. Nothing in this app sets `<input size>` — it measures a field
 * in characters, which is a width, and every field here is `w-full`.
 */
export function Input({
  size = "md",
  surface = "panel",
  className,
  ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, "size"> & {
  size?: keyof typeof CONTROL_SIZES;
  surface?: keyof typeof CONTROL_SURFACES;
}) {
  return (
    <input
      {...rest}
      className={cx(
        CONTROL_BASE,
        CONTROL_SIZES[size],
        CONTROL_SURFACES[surface],
        "font-sans",
        className,
      )}
    />
  );
}

export function Textarea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...rest} className={cx(CONTROL, className)} />;
}

export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <div className="relative">
      <select
        {...rest}
        className={cx(CONTROL, "peer cursor-pointer appearance-none pr-8", className)}
      >
        {children}
      </select>
      <Icon
        name="chevron"
        className="pointer-events-none absolute top-1/2 right-2.5 size-3.5 -translate-y-1/2 text-fg-subtle peer-disabled:opacity-50"
      />
    </div>
  );
}

/**
 * An on/off control that commits immediately.
 *
 * Distinct from the checkboxes elsewhere, and the distinction is the point: a
 * checkbox states an intention that a later Save acts on, a switch *is* the
 * action. Backups are the second kind — there is no form around them, and a
 * button reading "Turn on backups" makes the reader work out the current state
 * from the label of the thing that would change it.
 *
 * The track is a pill where this system is otherwise square, because the pill is
 * the affordance: a square one reads as a checkbox, which is the exact meaning
 * being avoided. `role="switch"` carries that to a screen reader, and the button
 * is padded well past the 24px track so a finger has the 44px the rest of the
 * system reserves for touch.
 */
export function Switch({
  checked,
  onChange,
  disabled = false,
  label,
  id,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  /** Required: the control carries no visible text of its own. */
  label: string;
  id?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      id={id}
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      /* Negative margin so the enlarged hit area does not push the row apart. */
      className="-m-2 shrink-0 rounded-md p-2 focus-visible:ring-2 focus-visible:ring-accent/60 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
    >
      <span
        className={cx(
          "flex h-6 w-11 items-center rounded-full border transition-colors duration-200 ease-smooth",
          checked ? "border-accent bg-accent" : "border-line-strong bg-raised",
        )}
      >
        <span
          className={cx(
            "size-4 rounded-full transition-transform duration-200 ease-smooth",
            checked ? "translate-x-[22px] bg-on-inverse" : "translate-x-[3px] bg-fg-subtle",
          )}
        />
      </span>
    </button>
  );
}

// ------------------------------------------------------------- containers

export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cx("card", className)}>{children}</div>;
}

export function CardHeader({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 border-b border-line px-5 py-3.5">
      <div>
        <h2 className="text-[13px] font-medium text-fg">{title}</h2>
        {description && <p className="mt-0.5 text-[13px] text-fg-subtle">{description}</p>}
      </div>
      {action}
    </div>
  );
}

export function PageHeader({
  title,
  description,
  action,
  tone = "default",
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  /**
   * `ai` marks the header as AI territory, which is the only thing iris does in
   * this app — the same rule the marketing site follows. It says which half of
   * the product you are in, never "this screen matters more". One section uses
   * it. If a second wants it, the territory grew; the colour did not become
   * available.
   */
  tone?: "default" | "ai";
}) {
  const ai = tone === "ai";

  return (
    <header
      className={cx(
        "flex flex-wrap items-end justify-between gap-4 border-b pb-5",
        /* The rule, and nothing else. This also carried an iris wash bled in
           from the leading edge — a `radial-gradient` of `--color-iris-soft` on
           a `before:` pseudo-element — and that wash was the one thing here that
           was decoration rather than signal. It tinted a band of the page behind
           the title without saying anything the coloured rule does not, and a
           page built out of hairlines is the wrong place for the only
           soft-focus object in the app.

           `relative isolate` went with it: the positioning context and the
           stacking context existed to hold that pseudo-element, and nothing
           else here needs either. */
        ai ? "border-iris/25" : "border-line",
      )}
    >
      {/* `min-w-0` because a flex child will not shrink below its longest
          unbreakable word by default, and these titles are app names and
          hostnames. Without it the header sets a floor wider than a phone and
          the whole page scrolls sideways. */}
      <div className="min-w-0">
        {/* Weight 400 with tight tracking. The 300 display weight belongs to the
            auth pages, which are the only place here set at marketing scale. */}
        <h1 className="text-[1.375rem] font-normal tracking-[-0.022em] break-words text-fg">
          {title}
        </h1>
        {description && <p className="mt-1 text-[13px] text-fg-muted">{description}</p>}
      </div>
      {action}
    </header>
  );
}

export function EmptyState({
  title,
  description,
  action,
  icon = "apps",
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  icon?: IconName;
}) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
      {/* Square, like every other container. Round is reserved for status dots
          and avatars — a circle here read as a leftover from somewhere else. */}
      <span className="grid size-10 place-items-center rounded-md border border-line bg-raised text-fg-subtle">
        <Icon name={icon} className="size-4" />
      </span>
      <h3 className="text-sm font-medium text-fg">{title}</h3>
      {description && (
        <p className="max-w-sm text-[13px] leading-6 text-fg-muted">{description}</p>
      )}
      {action && <div className="mt-1">{action}</div>}
    </div>
  );
}

type AlertTone = "error" | "info" | "success" | "warning";

/*
 * Tinted at 8% over the canvas with a hairline at 30%. The old version filled
 * with `red-50`/`emerald-50` — pale washes from the raw palette that only ever
 * rendered as their `dark:` half anyway.
 */
const ALERT_TONES: Record<AlertTone, { styles: string; icon: IconName }> = {
  error: { styles: "border-danger/30 bg-danger/8 text-danger", icon: "warning" },
  info: { styles: "border-accent/30 bg-accent/8 text-accent", icon: "warning" },
  success: { styles: "border-mint/30 bg-mint/8 text-mint", icon: "check" },
  warning: { styles: "border-warn/30 bg-warn/8 text-warn", icon: "warning" },
};

export function Alert({ tone = "error", children }: { tone?: AlertTone; children: ReactNode }) {
  const { styles, icon } = ALERT_TONES[tone];

  return (
    <div
      role="alert"
      className={cx("flex items-start gap-2.5 rounded-md border px-3 py-2.5 text-[13px]", styles)}
    >
      <Icon name={icon} className="mt-0.5 size-3.5 shrink-0" />
      <div className="min-w-0">{children}</div>
    </div>
  );
}

export function LoadingBlock({ label }: { label?: string }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center gap-2 py-12 text-[13px] text-fg-subtle">
      <Spinner className="size-3.5" />
      {label ?? t("loading")}
    </div>
  );
}

/** Grey block standing in for content that has not arrived yet. */
export function Skeleton({ className }: { className?: string }) {
  return <div className={cx("skeleton animate-pulse", className ?? "h-4 w-full")} />;
}

// ------------------------------------------------------------------ table

/**
 * The narrowest viewport a column survives on.
 *
 * Rather than reflow rows into cards on a phone — which stops being a table,
 * and the whole value of one is scanning a column down the page — each column
 * declares the width it earns its place at, and the rest drop out.
 *
 * Choosing which: keep what identifies the row and what you came to check.
 * Everything you would only read once you had already found the row is `md` or
 * `lg`. Nothing that is the *only* copy of a fact on the screen should be
 * hidden — if a column carries something unreachable elsewhere, it stays, and
 * the table scrolls.
 */
export type ColumnPriority = "always" | "sm" | "md" | "lg";

/* Written out rather than interpolated: Tailwind scans source text, so a
   template literal would emit nothing. */
const COLUMN_PRIORITY: Record<ColumnPriority, string> = {
  always: "",
  sm: "hidden sm:table-cell",
  md: "hidden md:table-cell",
  lg: "hidden lg:table-cell",
};

export function Table({ head, children }: { head: ReactNode; children: ReactNode }) {
  return (
    /*
     * Wide tables scroll inside their own container; the page never does. The
     * hard `min-w-[36rem]` this used to carry is gone — it guaranteed a
     * horizontal scrollbar on every phone regardless of how few columns were
     * actually showing. Column priorities set the width now, and
     * `scroll-shadow-x` says so when the result still overflows.
     */
    <div className="scroll-shadow-x overflow-x-auto">
      <table className="w-full border-collapse text-left">
        <thead className="border-b border-line text-[11px] tracking-[0.08em] text-fg-subtle uppercase">
          {head}
        </thead>
        <tbody className="divide-y divide-line">{children}</tbody>
      </table>
    </div>
  );
}

export function Th({
  children,
  className,
  priority = "always",
}: {
  children?: ReactNode;
  className?: string;
  /** Must match the `Td` beneath it, or the row shears away from its header. */
  priority?: ColumnPriority;
}) {
  return (
    <th className={cx("px-4 py-2.5 font-medium", COLUMN_PRIORITY[priority], className)}>
      {children}
    </th>
  );
}

export function Td({
  children,
  className,
  title,
  priority = "always",
  colSpan,
}: {
  children?: ReactNode;
  className?: string;
  /** Native tooltip — used to hang an exact timestamp off a relative one. */
  title?: string;
  /** Must match the `Th` above it, or the row shears away from its header. */
  priority?: ColumnPriority;
  /**
   * For a cell that answers what two headers ask.
   *
   * The model catalogue needs it: a generation model is priced per image or per
   * second rather than per million tokens in and out, so one value replaces the
   * pair and takes their combined width. The header stays as it is — those two
   * columns are still what a rate-carded model shows, and most rows are.
   */
  colSpan?: number;
}) {
  return (
    <td
      className={cx("cell", COLUMN_PRIORITY[priority], className)}
      title={title}
      colSpan={colSpan}
    >
      {children}
    </td>
  );
}

// ------------------------------------------------------------------- tabs

/**
 * Underlined tabs for a detail page. Routed rather than stateful, so a tab is
 * a real URL you can link to and reload into.
 */
export function Tabs({ items }: { items: { to: string; label: string; end?: boolean }[] }) {
  return (
    <div className="-mx-4 [--scroll-shadow-bg:var(--color-canvas)] scroll-shadow-x overflow-x-auto px-4 sm:mx-0 sm:px-0">
      <nav className="flex min-w-max gap-4 border-b border-line">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              cx(
                "-mb-px border-b px-0.5 py-2.5 text-[13px] font-medium whitespace-nowrap transition-colors duration-200 ease-smooth",
                isActive
                  ? "border-fg text-fg"
                  : "border-transparent text-fg-subtle hover:text-fg",
              )
            }
          >
            {item.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

/**
 * `Tabs` turned on its side: the section list beside a settings page rather
 * than above a detail page's panels.
 *
 * One `nav`, not one per breakpoint. The obvious build is a vertical list at
 * `lg` and a `Tabs` underneath it with the other hidden, and it puts every
 * destination in the document twice — which a screen reader reads out twice,
 * because `hidden` on the visual copy is a class and not a fact about the tree.
 * So the list is one set of links and only the *indicator* moves: a rule under
 * the label when they are in a row, a rule beside it when they are in a column.
 *
 * Accent for the active item and an accent rule marking it, which is the rail's
 * idiom one level down — the same colour saying the same thing about where you
 * are, rather than a second convention for a smaller nav.
 */
export function SectionNav({
  items,
}: {
  items: { to: string; label: string; end?: boolean }[];
}) {
  return (
    <nav
      className={cx(
        // In a row and scrollable below `lg`, in a column beside the content at
        // `lg`. The negative margin lets the row bleed to the page edge so a
        // scrolled tab is not clipped mid-label by the container's padding.
        "-mx-4 flex gap-4 overflow-x-auto border-b border-line px-4 sm:mx-0 sm:px-0",
        "[--scroll-shadow-bg:var(--color-canvas)] scroll-shadow-x",
        "lg:mx-0 lg:flex-col lg:gap-0.5 lg:overflow-visible lg:border-b-0 lg:bg-none lg:px-0",
      )}
    >
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          className={({ isActive }) =>
            cx(
              "relative -mb-px border-b px-0.5 py-2.5 text-[13px] font-medium whitespace-nowrap transition-colors duration-200 ease-smooth",
              "lg:mb-0 lg:border-b-0 lg:px-2.5 lg:py-1.5",
              isActive
                ? "border-accent text-accent"
                : "border-transparent text-fg-subtle hover:text-fg",
            )
          }
        >
          {({ isActive }) => (
            <>
              {isActive && (
                <span
                  aria-hidden="true"
                  className="absolute top-1/2 left-0 hidden h-4 w-0.5 -translate-y-1/2 bg-accent lg:block"
                />
              )}
              {item.label}
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

// ---------------------------------------------------------------- badges

/**
 * A coloured dot carries the status as well as the label does, and survives
 * being skimmed — the eye finds a red dot in a column far faster than it reads
 * the word "error".
 */
function badge(
  styles: string,
  dot: string | null,
  content: ReactNode,
  extra?: string,
  title?: string,
) {
  return (
    /* Square, like everything else. The dot inside stays round — it is a dot.
       A column of pills on an otherwise square page is the kind of leftover
       that reads as unfinished rather than as a deliberate exception. */
    <span
      title={title}
      className={cx(
        "inline-flex items-center gap-1.5 border px-1.5 py-0.5 text-[11px] font-medium",
        styles,
        extra,
      )}
    >
      {dot && <span aria-hidden="true" className={cx("size-1.5 rounded-full", dot)} />}
      {content}
    </span>
  );
}

/*
 * Status tones come off the semantic tokens now. `mint` in particular was
 * declared as the reserved "live and healthy" signal and then never used by the
 * one component that means it — every running app was rendering raw
 * `emerald-500`. If a thing is up, it is mint, and nothing else on the page is.
 */
const NEUTRAL = "border-line bg-raised text-fg-muted";
const GOOD = "border-mint/25 bg-mint/8 text-mint";
const WARN = "border-warn/25 bg-warn/8 text-warn";
const BAD = "border-danger/25 bg-danger/8 text-danger";

const APP_STATE: Record<AppState, { styles: string; dot: string }> = {
  draft: { styles: NEUTRAL, dot: "bg-fg-subtle" },
  deploying: { styles: WARN, dot: "bg-warn" },
  running: { styles: GOOD, dot: "bg-mint" },
  stopped: { styles: NEUTRAL, dot: "bg-fg-subtle" },
  error: { styles: BAD, dot: "bg-danger" },
};

/**
 * `stale` means the deployment engine could not be reached, so this is our own
 * record rather than the truth. It is dimmed and says so on hover — showing it
 * at full confidence would be claiming something we did not verify.
 */
export function StateBadge({ state, stale = false }: { state: AppState; stale?: boolean }) {
  const { t } = useTranslation("status");
  const { styles, dot } = APP_STATE[state];
  const spinning = state === "deploying" && !stale;
  /*
   * The word, not the API's value with a `capitalize` over it.
   *
   * This rendered `state` directly — "running", "deploying" — and leaned on CSS
   * for the capital letter, which works in exactly one language and only
   * because English capitalises the way it does. A status somebody reads on a
   * badge is copy, whatever the field behind it is called, so the badge looks
   * the word up and the class goes with it.
   */
  return badge(
    cx(styles, stale && "opacity-60"),
    spinning ? null : dot,
    <>
      {spinning && <Spinner className="size-2.5" />}
      {t(`app.${state}`)}
      {stale && <span className="text-[0.9em] normal-case opacity-80">{t("stale")}</span>}
    </>,
    undefined,
    stale ? t("staleTitle") : undefined,
  );
}

const DEPLOY_STATUS: Record<DeploymentStatus, { styles: string; dot: string }> = {
  running: { styles: WARN, dot: "bg-warn" },
  done: { styles: GOOD, dot: "bg-mint" },
  error: { styles: BAD, dot: "bg-danger" },
  idle: { styles: NEUTRAL, dot: "bg-fg-subtle" },
};

export function DeploymentBadge({ status }: { status: DeploymentStatus }) {
  const { styles, dot } = DEPLOY_STATUS[status];
  return badge(
    styles,
    status === "running" ? null : dot,
    <>
      {status === "running" && <Spinner className="size-2.5" />}
      {status}
    </>,
    "capitalize",
  );
}

/**
 * The four tones the status badges share, named rather than restated.
 *
 * A caller that computes its own state — a tunnel's, which is two signals
 * folded into one line — picks a tone here instead of passing Tailwind classes
 * around, so the reserved meaning of `mint` stays with the tokens above.
 */
export type StatusTone = "good" | "warn" | "bad" | "neutral";

const STATUS_TONE: Record<StatusTone, { styles: string; dot: string }> = {
  good: { styles: GOOD, dot: "bg-mint" },
  warn: { styles: WARN, dot: "bg-warn" },
  bad: { styles: BAD, dot: "bg-danger" },
  neutral: { styles: NEUTRAL, dot: "bg-fg-subtle" },
};

export function StatusBadge({
  tone,
  label,
  title,
  stale = false,
}: {
  tone: StatusTone;
  label: string;
  title?: string;
  /**
   * The value is our own record, not a reading — the same treatment
   * `StateBadge` gives it, for the same reason: showing it at full confidence
   * would be claiming something nobody verified.
   */
  stale?: boolean;
}) {
  const { t } = useTranslation("status");
  const { styles, dot } = STATUS_TONE[tone];
  return badge(
    cx(styles, stale && "opacity-60"),
    dot,
    <>
      {label}
      {stale && <span className="text-[0.9em] opacity-80">{t("stale")}</span>}
    </>,
    undefined,
    stale ? t("staleTitle") : title,
  );
}

export function RoleBadge({ role }: { role: string }) {
  const { t } = useTranslation("status");
  /* The role in words, with no `capitalize` behind it — see `StateBadge`. An
     unknown role falls through to its own value, which is better than an empty
     badge if the API ever grows one this build has not heard of. */
  return badge(NEUTRAL, null, t(`role.${role}` as "role.owner", { defaultValue: role }));
}

/* `children` is optional so this can be handed to a <Trans> components map,
   which injects them at render time and cannot say so to the typechecker. */
export function Mono({ children, className }: { children?: ReactNode; className?: string }) {
  return <span className={cx("ident", className)}>{children}</span>;
}

/**
 * What somebody has to type to confirm, set apart from the sentence around it.
 *
 * A name can hold spaces, quotes or the very words of the prompt — "Type Shop
 * copy to confirm" does not say where the name ends — so the phrase is a chip,
 * in mono because it is typed character for character, breaking anywhere so a
 * long one wraps inside the dialog rather than running out of it. Every typed
 * confirmation uses it, and none also puts the phrase in the input as a
 * placeholder: a faint copy of the answer inside the box reads as already
 * typed.
 */
export function TypedPhrase({ children }: { children: ReactNode }) {
  return (
    <span className="rounded border border-line-strong bg-raised px-1.5 py-px font-mono text-[0.92em] break-all text-fg">
      {children}
    </span>
  );
}

/** Stands in for the phrase while a prompt is translated, then marks where to split it. */
const PHRASE_SLOT = "\u2063";

/**
 * A translated prompt with its phrase as a `TypedPhrase`. The sentence is
 * translated with a marker in the phrase's place and split there, so each
 * language keeps its own word order: "Type [Shop] to confirm", "Ketik [Shop]
 * untuk konfirmasi".
 */
export function ConfirmPrompt({ sentence, phrase }: { sentence: (slot: string) => string; phrase: string }) {
  const text = sentence(PHRASE_SLOT);
  const at = text.indexOf(PHRASE_SLOT);
  if (at === -1) return <>{text}</>;
  // Nothing to type yet — the name has not loaded — is no chip, not an empty one.
  if (!phrase) return <>{text.replace(PHRASE_SLOT, "")}</>;
  return (
    <>
      {text.slice(0, at)}
      <TypedPhrase>{phrase}</TypedPhrase>
      {text.slice(at + PHRASE_SLOT.length)}
    </>
  );
}

// --------------------------------------------------------------- dropdown

export interface DropdownProps {
  trigger: ReactNode;
  /** Extra classes for the trigger button. */
  triggerClassName?: string;
  /**
   * Which way the panel opens. `top` is not decoration — a trigger sitting at
   * the bottom of a full-height rail has no room below it, and a panel that
   * opens down there lands off the bottom of the viewport.
   */
  side?: "top" | "bottom";
  /** Horizontal anchoring. `stretch` matches the trigger's own width. */
  align?: "start" | "end" | "stretch";
  /** Overrides for the panel. Geometry belongs in `side`/`align`, not here. */
  menuClassName?: string;
  children: ReactNode;
}

const SIDES = {
  bottom: "top-full mt-1.5",
  top: "bottom-full mb-1.5",
} as const;

const ALIGNS = {
  start: "left-0",
  end: "right-0",
  stretch: "left-0 right-0",
} as const;

/**
 * A popover menu anchored to a trigger button.
 *
 * Click the trigger to toggle, click outside or press Escape to close, and any
 * click inside the panel closes it too — the selection happens on the same
 * click. Arrow keys walk the items, Home and End jump to the ends, and closing
 * hands focus back to the trigger so the tab order does not reset to the top of
 * the page.
 *
 * `side` and `align` exist because the alternative was every call site passing
 * its own pile of `top-*`/`left-*` overrides through `menuClassName` — which is
 * how the profile menu ended up opening off the bottom of the screen on any
 * viewport narrower than `lg`.
 *
 * Children are expected to be links or buttons: that is what the arrow keys
 * move between, and what `role="menu"` here implies.
 */
export function Dropdown({
  trigger,
  triggerClassName,
  side = "bottom",
  align = "start",
  menuClassName,
  children,
}: DropdownProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const close = (returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  };

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    window.addEventListener("pointerdown", onPointerDown);
    return () => window.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  /* Focus the first item on open. The global `:focus-visible` rule means a mouse
     user never sees the ring, so this costs nothing and makes the menu keyboard
     drivable the moment it appears. */
  useEffect(() => {
    if (open) items()[0]?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function items(): HTMLElement[] {
    return Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>("a[href], button:not([disabled])") ?? [],
    );
  }

  function move(delta: number) {
    const list = items();
    if (list.length === 0) return;
    const at = list.findIndex((item) => item === document.activeElement);
    const next = at === -1 ? 0 : (at + delta + list.length) % list.length;
    list[next]?.focus();
  }

  const onMenuKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case "Escape":
        event.preventDefault();
        close(true);
        break;
      case "ArrowDown":
        event.preventDefault();
        move(1);
        break;
      case "ArrowUp":
        event.preventDefault();
        move(-1);
        break;
      case "Home":
        event.preventDefault();
        items()[0]?.focus();
        break;
      case "End":
        event.preventDefault();
        items().at(-1)?.focus();
        break;
      case "Tab":
        // Tabbing out of a menu means leaving it, not cycling inside it.
        close(false);
        break;
      default:
        break;
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        onKeyDown={(event) => {
          // Down-arrow opens, matching every other menu the customer has used.
          if (!open && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
            event.preventDefault();
            setOpen(true);
          }
        }}
        className={cx(
          "inline-flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[13px] font-medium transition-colors duration-200 ease-smooth",
          triggerClassName,
        )}
      >
        {trigger}
      </button>

      {open && (
        <div
          ref={menuRef}
          role="menu"
          onClick={() => setOpen(false)}
          onKeyDown={onMenuKeyDown}
          /*
           * A popover is the one place a shadow earns its keep: `raised` over
           * `surface` is a six-value luminance step, which is not enough on its
           * own to say "this floats above what it covers". The hairline does the
           * rest, at `line-strong` rather than `line` — this panel routinely
           * opens over the rail it lives in, where a `line` edge disappeared.
           *
           * `shadow-lg` is still the near-nothing token, not the old
           * `shadow-xl shadow-ink-950/10` — a *coloured* shadow, which was the
           * actual offence.
           */
          className={cx(
            "absolute z-50 min-w-56 rounded-md border border-line-strong bg-raised p-1 shadow-lg",
            side === "top" ? "animate-dropdown-up" : "animate-dropdown-in",
            SIDES[side],
            ALIGNS[align],
            menuClassName,
          )}
        >
          {children}
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------------------- combobox

export interface ComboboxOption {
  value: string;
  label: string;
  /** Shown dimmed at the end of the row. Searched along with the label. */
  hint?: string;
}

export interface ComboboxGroup {
  label: string;
  options: ComboboxOption[];
}

/** The value is searched too, so `America/New_York` is findable by its id. */
function matches(option: ComboboxOption, query: string): boolean {
  return matchesQuery(`${option.label} ${option.hint ?? ""} ${option.value}`, query);
}

/**
 * A select you can type into.
 *
 * The native `<select>` this replaces is fine at eight options and unusable at
 * four hundred: the timezone list is the whole IANA database, and an
 * organization with thirty apps is a scroll to find one by eye. Type-ahead in a
 * native select matches only the first characters of an option's text, so
 * "new york" finds nothing in `America/New_York` and neither does "york".
 *
 * The editable-combobox pattern, which is what the ARIA authoring practices
 * call this: `role="combobox"` on the input, the list referenced by
 * `aria-controls`, and the highlighted row named by `aria-activedescendant`
 * rather than focused. Focus stays in the input the whole time, which is what
 * lets somebody keep typing while arrowing through results.
 *
 * Falls back to a plain value: when it is closed the input shows the selected
 * label rather than the query, so it reads as a field with a value rather than
 * a search box somebody abandoned.
 */
export function Combobox({
  id,
  value,
  onChange,
  groups,
  placeholder,
  emptyLabel,
  disabled,
}: {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  groups: ComboboxGroup[];
  placeholder?: string;
  emptyLabel?: string;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);

  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const listId = `${id ?? "combobox"}-list`;

  const selected = groups.flatMap((group) => group.options).find((option) => option.value === value);

  // Only filter while open. A closed field shows its value, and filtering the
  // list by a label the customer never typed would hide everything else the
  // moment they reopened it.
  const visible = groups
    .map((group) => ({ ...group, options: group.options.filter((o) => matches(o, open ? query : "")) }))
    .filter((group) => group.options.length > 0);

  const flat = visible.flatMap((group) => group.options);
  const activeOption = flat[Math.min(active, flat.length - 1)];

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    window.addEventListener("pointerdown", onPointerDown);
    return () => window.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  /* Keeps the highlighted row on screen while arrowing. Four hundred options is
     far past what fits, so without this the cursor walks out of view. */
  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector<HTMLElement>('[data-active="true"]')?.scrollIntoView({
      block: "nearest",
    });
  }, [open, active, query]);

  function commit(option: ComboboxOption | undefined) {
    if (!option) return;
    onChange(option.value);
    setOpen(false);
    setQuery("");
  }

  function start() {
    if (disabled) return;
    setOpen(true);
    setQuery("");
    // Start on the current value, so opening and pressing Enter is a no-op
    // rather than a silent change to whatever happens to be first.
    const at = flat.findIndex((option) => option.value === value);
    setActive(at === -1 ? 0 : at);
  }

  function onKeyDown(event: React.KeyboardEvent) {
    if (!open && (event.key === "ArrowDown" || event.key === "Enter")) {
      event.preventDefault();
      start();
      return;
    }
    if (!open) return;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setActive((at) => (flat.length === 0 ? 0 : (at + 1) % flat.length));
        break;
      case "ArrowUp":
        event.preventDefault();
        setActive((at) => (flat.length === 0 ? 0 : (at - 1 + flat.length) % flat.length));
        break;
      case "Home":
        event.preventDefault();
        setActive(0);
        break;
      case "End":
        event.preventDefault();
        setActive(Math.max(0, flat.length - 1));
        break;
      case "Enter":
        event.preventDefault();
        commit(activeOption);
        break;
      case "Escape":
        event.preventDefault();
        setOpen(false);
        setQuery("");
        break;
      case "Tab":
        // Leaving the field is leaving it, not choosing whatever was highlighted.
        setOpen(false);
        setQuery("");
        break;
      default:
        break;
    }
  }

  let index = -1;

  return (
    <div ref={rootRef} className="relative">
      <input
        {...{ id }}
        ref={inputRef}
        type="text"
        role="combobox"
        autoComplete="off"
        spellCheck={false}
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && activeOption ? `${listId}-${activeOption.value}` : undefined}
        disabled={disabled}
        value={open ? query : (selected?.label ?? "")}
        placeholder={
          open ? (placeholder ?? t("search")) : (selected?.label ?? placeholder ?? t("search"))
        }
        onChange={(event) => {
          setQuery(event.target.value);
          setActive(0);
          if (!open) setOpen(true);
        }}
        onFocus={start}
        onClick={start}
        onKeyDown={onKeyDown}
        className={cx(CONTROL_BASE, CONTROL_SIZES.md, CONTROL_SURFACES.panel, "cursor-text pr-8")}
      />
      <Icon
        name="chevron"
        className="pointer-events-none absolute top-1/2 right-2.5 size-3.5 -translate-y-1/2 text-fg-subtle"
      />

      {open && (
        <div
          ref={listRef}
          id={listId}
          role="listbox"
          /* The same raised popover the dropdown uses: a shadow earns its keep
             here because the list sits over content rather than in the flow. */
          className="absolute z-30 mt-1 max-h-72 w-full overflow-y-auto rounded-md border border-line bg-raised py-1 shadow-lg"
        >
          {flat.length === 0 ? (
            <p className="px-3 py-2 text-[13px] text-fg-subtle">
              {emptyLabel ?? t("noMatch")}
            </p>
          ) : (
            visible.map((group) => (
              <div key={group.label}>
                {/* Only when there is more than one, so a filtered list that
                    narrowed to a single region does not keep a header over a
                    row it no longer distinguishes. */}
                {visible.length > 1 && <p className="micro px-3 pt-1.5 pb-1">{group.label}</p>}
                {group.options.map((option) => {
                  index += 1;
                  const isActive = index === Math.min(active, flat.length - 1);
                  const isSelected = option.value === value;
                  const at = index;
                  return (
                    <button
                      key={option.value}
                      id={`${listId}-${option.value}`}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      data-active={isActive}
                      onMouseMove={() => setActive(at)}
                      onClick={() => commit(option)}
                      className={cx(
                        "flex w-full items-center gap-2 px-3 py-1.5 text-left transition-colors duration-100",
                        isActive ? "bg-canvas" : "hover:bg-canvas/60",
                      )}
                    >
                      <span className="min-w-0 flex-1 truncate text-[13px] text-fg">
                        {option.label}
                      </span>
                      {option.hint && (
                        <span className="shrink-0 truncate text-[11px] text-fg-subtle">
                          {option.hint}
                        </span>
                      )}
                      {isSelected && <Icon name="check" className="size-3.5 shrink-0 text-accent" />}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
